import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, FileText, Layers, Eye, Send, RefreshCw, Sparkles,
  Check, RotateCcw, Loader2,
} from "lucide-react";

function gradeColor(grade) {
  const fam = (grade || "")[0];
  if (fam === "A") return "bg-green-600 text-white";
  if (fam === "B") return "bg-lime-600 text-white";
  return "bg-orange-600 text-white";
}

function RichText({ text }) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**")
          ? <strong key={i}>{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

function GradeBadge({ grade, testid }) {
  if (!grade) return null;
  return (
    <span
      data-testid={testid}
      className={`inline-flex min-w-[3rem] items-center justify-center rounded-md px-2 py-1 font-display text-lg font-bold ${gradeColor(grade)}`}
    >
      {grade}
    </span>
  );
}

function Assistant({ month, name, grade, onGradeChange, onChanged }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [applying, setApplying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoaded(false);
    api.get(`/reports/commercial/assistant/history?month=${month}&name=${encodeURIComponent(name)}`)
      .then((r) => { if (active) { setMessages(r.data.messages || []); setLoaded(true); } })
      .catch(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [month, name]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/reports/commercial/assistant/chat", { month, name, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, proposal: data.proposal }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assistente non disponibile");
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Assistente non disponibile al momento." }]);
    } finally {
      setSending(false);
    }
  };

  const apply = async (proposal, idx) => {
    setApplying(true);
    try {
      const { data } = await api.post("/reports/commercial/assistant/apply", {
        month, name, action: { exclusions: proposal.exclusions },
      });
      toast.success(`Voto aggiornato: ${data.grade}`);
      onGradeChange?.(data.grade);
      onChanged?.();
      setMessages((m) => m.map((msg, i) => i === idx ? { ...msg, proposal: { ...msg.proposal, applied: true } } : msg));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile applicare");
    } finally {
      setApplying(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Azzerare tutte le esclusioni e tornare al voto originale?")) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/reports/commercial/assistant/reset?month=${month}&name=${encodeURIComponent(name)}`);
      toast.success(`Voto ripristinato: ${data.grade}`);
      onGradeChange?.(data.grade);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile ripristinare");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="flex h-full flex-col" data-testid="pagella-assistant">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="h-4 w-4 text-primary" /> Assistente AI
        </div>
        <div className="flex items-center gap-2">
          <GradeBadge grade={grade} testid="assistant-current-grade" />
          <Button
            size="sm" variant="ghost" onClick={reset} disabled={applying}
            data-testid="assistant-reset-btn" title="Ripristina voto originale"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1 px-4" data-testid="assistant-messages">
        <div className="space-y-3 py-4">
          {!loaded ? (
            <Skeleton className="h-16 w-full rounded-lg" />
          ) : messages.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Chiedi all'assistente di analizzare i dati o di non considerare alcuni giorni sotto soglia o lead lasciati indietro.
              Es: <em>"Non considerare i lead lasciati indietro di questo mese"</em>.
            </p>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                    m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                  }`}
                  data-testid={`assistant-msg-${m.role}-${i}`}
                >
                  <RichText text={m.content} />
                  {m.proposal && (
                    <div className="mt-3 rounded-md border border-border bg-background p-3 text-foreground">
                      <p className="text-xs font-medium">{m.proposal.summary}</p>
                      <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        Voto: <GradeBadge grade={m.proposal.current_grade} />
                        <span>→</span>
                        <GradeBadge grade={m.proposal.new_grade} />
                      </p>
                      <Button
                        size="sm" className="mt-2 w-full"
                        disabled={applying || m.proposal.applied}
                        onClick={() => apply(m.proposal, i)}
                        data-testid={`assistant-apply-${i}`}
                      >
                        {m.proposal.applied
                          ? <><Check className="mr-2 h-4 w-4" /> Applicato</>
                          : <>Conferma esclusione e aggiorna voto</>}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Sto pensando…
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      <div className="border-t border-border p-3">
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Scrivi un messaggio…"
            className="max-h-28 min-h-[40px] resize-none"
            data-testid="assistant-input"
          />
          <Button size="icon" onClick={send} disabled={sending || !input.trim()} data-testid="assistant-send-btn">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ScorecardDialog({ open, onClose, item, month, onChanged }) {
  const [grade, setGrade] = useState(item?.grade);
  useEffect(() => { setGrade(item?.grade); }, [item]);
  if (!item) return null;
  const isGeneral = item.name === "__general__";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl gap-0 p-0" data-testid="pagella-viewer">
        <DialogHeader className="border-b border-border px-5 py-3">
          <DialogTitle className="flex items-center gap-3 text-base">
            {item.title}
            {!isGeneral && <GradeBadge grade={grade} testid="viewer-grade" />}
          </DialogTitle>
        </DialogHeader>
        <div className={isGeneral ? "" : "grid grid-cols-1 md:grid-cols-[1fr_380px]"}>
          <iframe
            title={item.title}
            srcDoc={item.html}
            className="h-[72vh] w-full bg-white"
            data-testid="pagella-iframe"
          />
          {!isGeneral && (
            <div className="h-[72vh] border-l border-border">
              <Assistant
                month={month}
                name={item.name}
                grade={grade}
                onGradeChange={setGrade}
                onChanged={onChanged}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Pagelle() {
  const [month, setMonth] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [sending, setSending] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const { data: months } = useQuery({
    queryKey: ["pagelle-months"],
    queryFn: async () => (await api.get("/reports/commercial/months")).data,
  });

  useEffect(() => {
    if (months && months.length && !month) setMonth((months[1] || months[0]).value);
  }, [months, month]);

  const { data, isLoading } = useQuery({
    queryKey: ["pagelle-archive", month],
    queryFn: async () => (await api.get(`/reports/commercial/archive?month=${month}`)).data,
    enabled: !!month,
  });

  const monthLabel = months?.find((m) => m.value === month)?.label || "";

  const sendTo = async (name) => {
    const who = name || "TUTTI i commerciali";
    if (!window.confirm(`Inviare la pagella di ${monthLabel} a ${who}? Verrà inviata un'email reale (con copia a te).`)) return;
    setSending(name || "all");
    try {
      const q = name ? `?name=${encodeURIComponent(name)}&month=${month}` : `?month=${month}`;
      await api.post(`/reports/commercial/send${q}`);
      toast.success(name ? `Pagella di ${monthLabel} inviata a ${name}` : `Pagelle di ${monthLabel} inviate a tutti i commerciali`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally {
      setSending(null);
    }
  };

  const regenerate = async () => {
    setRefreshing(true);
    try {
      const res = await api.get(`/reports/commercial/archive?month=${month}&refresh=true`);
      queryClient.setQueryData(["pagelle-archive", month], res.data);
      toast.success("Pagelle rigenerate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rigenerazione non riuscita");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pagelle-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight">
            <GraduationCap className="h-6 w-6 text-primary" /> Pagelle commerciali
          </h1>
          <p className="text-sm text-muted-foreground">Storico mensile: apri e consulta le pagelle di ogni commerciale e il riepilogo generale.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            data-testid="pagelle-regenerate"
            disabled={refreshing || !month}
            onClick={regenerate}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Rigenero…" : "Rigenera"}
          </Button>
          <Button
            variant="default"
            data-testid="pagelle-send-all"
            disabled={!!sending || !month}
            onClick={() => sendTo(null)}
          >
            <Send className="mr-2 h-4 w-4" /> {sending === "all" ? "Invio…" : "Invia a tutti i commerciali"}
          </Button>
          <Select value={month || ""} onValueChange={setMonth}>
            <SelectTrigger className="w-[200px]" data-testid="pagelle-month-select">
              <SelectValue placeholder="Seleziona mese" />
            </SelectTrigger>
            <SelectContent>
              {(months || []).map((m) => (
                <SelectItem key={m.value} value={m.value} data-testid={`pagelle-month-${m.value}`}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(data?.items || []).map((it) => (
            <Card key={it.name} className="transition-shadow hover:shadow-md" data-testid={`pagella-card-${it.name.replace(/\s/g, "-")}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between gap-2 text-base">
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" /> {it.name}
                  </span>
                  <GradeBadge grade={it.grade} testid={`pagella-grade-${it.name.replace(/\s/g, "-")}`} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-1 line-clamp-2 text-xs text-muted-foreground">{it.subject}</p>
                {(it.exclusions?.weak_days_excluded?.length || it.exclusions?.lasciati_excluded) ? (
                  <Badge variant="secondary" className="mb-3 text-[10px]" data-testid={`pagella-override-${it.name.replace(/\s/g, "-")}`}>
                    Voto rettificato
                  </Badge>
                ) : <div className="mb-3" />}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    data-testid={`pagella-open-${it.name.replace(/\s/g, "-")}`}
                    onClick={() => setViewer({ name: it.name, title: `${it.name} — ${monthLabel}`, html: it.html, grade: it.grade })}
                  >
                    <Eye className="mr-2 h-4 w-4" /> Apri
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`pagella-send-${it.name.replace(/\s/g, "-")}`}
                    disabled={!!sending}
                    onClick={() => sendTo(it.name)}
                  >
                    <Send className="mr-2 h-4 w-4" /> {sending === it.name ? "Invio…" : "Invia"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {data?.general && (
            <Card className="border-primary/40 bg-primary/5 transition-shadow hover:shadow-md" data-testid="pagella-card-generale">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Layers className="h-4 w-4 text-primary" /> Riepilogo generale
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-3 text-xs text-muted-foreground">Tutte le pagelle del mese in un'unica vista.</p>
                <Button
                  size="sm"
                  className="w-full"
                  data-testid="pagella-open-generale"
                  onClick={() => setViewer({ name: "__general__", title: `Riepilogo generale — ${monthLabel}`, html: data.general.html })}
                >
                  <Eye className="mr-2 h-4 w-4" /> Apri riepilogo
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <ScorecardDialog
        open={!!viewer}
        onClose={() => setViewer(null)}
        item={viewer}
        month={month}
        onChanged={() => queryClient.invalidateQueries({ queryKey: ["pagelle-archive", month] })}
      />
    </div>
  );
}
